import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { TextareaInput } from "@/components/ui/Field";
import { NativeSelectField } from "@/components/ui/NativeSelect";
import { TimesheetBillingNotesPanel } from "@/features/timesheetFlow/components/TimesheetBillingNotesPanel";
import { TimesheetFlowCard } from "@/features/timesheetFlow/components/TimesheetFlowCard";
import type { TimesheetPermissions } from "@/features/timesheetFlow/timesheetPermissions";
import {
  gdProjectInvoiceOptionLabel,
  heroInvoiceOptionLabel,
  type GdProjectInvoiceOption,
  type HeroProjectInvoiceOption,
} from "@/features/timesheetFlow/timesheetFlowApi";
import { ApiError } from "@/lib/apiClient";
import { getDokument } from "@/lib/dokumente";
import { ensureDokumentStandardtexte } from "@/lib/gdStandardtexte";
import {
  assignLieferscheinBilling,
  createLieferscheinBillingQuestions,
  createLieferscheinInvoiceDraft,
  getLieferscheinDetail,
  linkLieferscheinHeroRechnung,
  linkLieferscheinRechnung,
  listLieferscheinHeroInvoices,
  listLieferscheinInvoiceCandidates,
  listLieferscheinProjectInvoices,
  markLieferscheinBillingSeen,
  respondLieferscheinBillingQuestion,
  type LieferscheinBillingQuestion,
  type LieferscheinDetail,
} from "@/lib/lieferscheine";
import { listBillingMembers, type BillingMember } from "@/lib/timesheets";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";

const COMPACT_CARD = "!p-2.5";
const COMPACT_TITLE = "!mb-2 text-sm";
const LINK_STATUSES = new Set(["signed", "assigned", "invoice_draft"]);

function memberLogin(m: BillingMember): string {
  return String(m.user_login ?? m.login ?? m.samAccountName ?? "").trim();
}

function memberLabel(m: BillingMember): string {
  const login = memberLogin(m);
  const name = m.displayName ? String(m.displayName) : "";
  return name ? `${name} (${login})` : login;
}

function questionIsOpen(q: LieferscheinBillingQuestion): boolean {
  const generalQuestion = String(q.question_text || "").trim();
  const generalAnswer = String(q.response_text || "").trim();
  if (generalQuestion && !generalAnswer) return true;
  const items = q.items || [];
  if (!items.length) return !q.responded_at;
  return items.some((item) => !(item.response_text || "").trim() && item.price == null);
}

function positionLabel(position: LieferscheinDetail["positions"][number]): string {
  const qty = [position.menge, position.einheit].filter((part) => part != null && part !== "").join(" ");
  const name = String(position.bezeichnung || "").trim();
  return [qty, name].filter(Boolean).join(" · ") || `Position ${position.id}`;
}

export function LieferscheinBillingTab({
  dokumentId,
  auftragId,
  permissions,
  onRefresh,
  onError,
  onOpenCountChange,
  onClose,
}: {
  dokumentId: number;
  auftragId: number;
  permissions: TimesheetPermissions;
  onRefresh: () => void;
  onError: (message: string) => void;
  onOpenCountChange?: (count: number) => void;
  onClose?: () => void;
}) {
  const { open: openDocumentEditor } = useDocumentEditor();
  const [detail, setDetail] = useState<LieferscheinDetail | null>(null);
  const [members, setMembers] = useState<BillingMember[]>([]);
  const [assigneeLogin, setAssigneeLogin] = useState("");
  const [generalQuestion, setGeneralQuestion] = useState("");
  const [positionQuestions, setPositionQuestions] = useState<Record<number, string>>({});
  const [answerText, setAnswerText] = useState<Record<number, string>>({});
  const [gdInvoices, setGdInvoices] = useState<GdProjectInvoiceOption[]>([]);
  const [heroInvoices, setHeroInvoices] = useState<HeroProjectInvoiceOption[]>([]);
  const [gdInvoicesLoading, setGdInvoicesLoading] = useState(false);
  const [heroInvoicesLoading, setHeroInvoicesLoading] = useState(false);
  const [gdInvoicesError, setGdInvoicesError] = useState<string | null>(null);
  const [heroInvoicesError, setHeroInvoicesError] = useState<string | null>(null);
  const [selectedGdInvoiceId, setSelectedGdInvoiceId] = useState("");
  const [selectedHeroInvoiceId, setSelectedHeroInvoiceId] = useState("");
  const [busy, setBusy] = useState(false);
  const [extraCandidates, setExtraCandidates] = useState<Array<{ id: number; display_number: string }>>(
    [],
  );
  const [candidateIds, setCandidateIds] = useState<number[]>([]);

  const questions = useMemo(() => detail?.billing_questions ?? [], [detail?.billing_questions]);
  const openQuestions = useMemo(() => questions.filter(questionIsOpen), [questions]);
  const showInvoiceLink = LINK_STATUSES.has(String(detail?.billing_status || ""));
  const canCreateDraft =
    permissions.canBilling &&
    (detail?.billing_status === "signed" || detail?.billing_status === "assigned") &&
    !detail?.gd_rechnung_id;
  const cardProps = { className: COMPACT_CARD, titleClassName: COMPACT_TITLE };

  useEffect(() => {
    onOpenCountChange?.(openQuestions.length);
  }, [onOpenCountChange, openQuestions.length]);

  const reload = useCallback(async () => {
    const data = await getLieferscheinDetail(dokumentId);
    setDetail(data);
    setAssigneeLogin(String(data.billing_assignee_login || ""));
    return data;
  }, [dokumentId]);

  useEffect(() => {
    let cancelled = false;
    void reload().catch((err: unknown) => {
      if (cancelled) return;
      onError(err instanceof Error ? err.message : "Abrechnung konnte nicht geladen werden.");
    });
    if (permissions.canBilling) {
      void markLieferscheinBillingSeen(dokumentId).catch(() => undefined);
      void listBillingMembers()
        .then((list) => {
          if (!cancelled) setMembers(list);
        })
        .catch(() => {
          if (!cancelled) setMembers([]);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [dokumentId, onError, permissions.canBilling, reload]);

  useEffect(() => {
    if (!permissions.canBilling || !showInvoiceLink) {
      setGdInvoices([]);
      setHeroInvoices([]);
      setSelectedGdInvoiceId("");
      setSelectedHeroInvoiceId("");
      return;
    }
    setGdInvoicesLoading(true);
    setHeroInvoicesLoading(true);
    setGdInvoicesError(null);
    setHeroInvoicesError(null);
    void listLieferscheinProjectInvoices(dokumentId)
      .then((data) => {
        setGdInvoices(data);
        const current = Number(detail?.gd_rechnung_id ?? 0);
        if (Number.isFinite(current) && current > 0) setSelectedGdInvoiceId(String(current));
      })
      .catch((err: unknown) => {
        setGdInvoices([]);
        setGdInvoicesError(
          err instanceof ApiError ? err.message : "Rechnungsliste (App) konnte nicht geladen werden.",
        );
      })
      .finally(() => setGdInvoicesLoading(false));
    void listLieferscheinHeroInvoices(dokumentId)
      .then((data) => {
        setHeroInvoices(data);
        const current = String(detail?.hero_invoice_customer_document_id || "").trim();
        if (!current) return;
        const match = data.find((inv) => {
          const customerId = String(inv.customer_document_id ?? inv.id ?? inv.document_id ?? "");
          return customerId === current;
        });
        if (match) {
          setSelectedHeroInvoiceId(
            String(match.customer_document_id ?? match.id ?? match.document_id ?? ""),
          );
        }
      })
      .catch((err: unknown) => {
        setHeroInvoices([]);
        setHeroInvoicesError(
          err instanceof ApiError
            ? err.message
            : "Hero-Rechnungen konnten nicht geladen werden.",
        );
      })
      .finally(() => setHeroInvoicesLoading(false));
  }, [
    dokumentId,
    permissions.canBilling,
    showInvoiceLink,
    detail?.gd_rechnung_id,
    detail?.hero_invoice_customer_document_id,
  ]);

  useEffect(() => {
    if (!canCreateDraft) {
      setExtraCandidates([]);
      setCandidateIds([]);
      return;
    }
    let cancelled = false;
    void listLieferscheinInvoiceCandidates(dokumentId)
      .then((data) => {
        if (!cancelled) setExtraCandidates(data?.candidates ?? []);
      })
      .catch(() => {
        if (!cancelled) setExtraCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canCreateDraft, dokumentId]);

  const runBusy = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const handleAssign = () => {
    if (!assigneeLogin.trim()) {
      onError("Bitte einen Bearbeiter auswählen.");
      return;
    }
    void runBusy(async () => {
      await assignLieferscheinBilling(dokumentId, assigneeLogin.trim());
      await reload();
      onRefresh();
    });
  };

  const handleSubmitQuestions = () => {
    const position_questions = (detail?.positions || [])
      .map((position) => {
        const text = (positionQuestions[position.id] || "").trim();
        if (!text) return null;
        return { position_id: position.id, question_text: text };
      })
      .filter(Boolean) as Array<{ position_id: number; question_text: string }>;
    const general = generalQuestion.trim();
    if (!general && position_questions.length === 0) {
      onError("Bitte mindestens eine Rückfrage eingeben.");
      return;
    }
    void runBusy(async () => {
      try {
        await createLieferscheinBillingQuestions(dokumentId, {
          general_question: general || undefined,
          position_questions,
        });
        setGeneralQuestion("");
        setPositionQuestions({});
        await reload();
        onRefresh();
      } catch (err: unknown) {
        onError(err instanceof Error ? err.message : "Rückfrage konnte nicht gesendet werden.");
      }
    });
  };

  const handleRespond = (questionId: number) => {
    const text = (answerText[questionId] || "").trim();
    if (!text) {
      onError("Bitte eine Antwort eingeben.");
      return;
    }
    void runBusy(async () => {
      try {
        await respondLieferscheinBillingQuestion(dokumentId, questionId, { response_text: text });
        setAnswerText((prev) => ({ ...prev, [questionId]: "" }));
        await reload();
        onRefresh();
      } catch (err: unknown) {
        onError(err instanceof Error ? err.message : "Antwort konnte nicht gespeichert werden.");
      }
    });
  };

  const handleInvoiceDraft = () => {
    void runBusy(async () => {
      try {
        const result = await createLieferscheinInvoiceDraft(dokumentId, [
          dokumentId,
          ...candidateIds,
        ]);
        onRefresh();
        if (result.gd_rechnung_id) {
          if (!result.already_exists) {
            try {
              await ensureDokumentStandardtexte(await getDokument(result.gd_rechnung_id));
            } catch {
              /* Editor zeigt den Entwurf trotzdem. */
            }
          }
          onClose?.();
          openDocumentEditor({ docId: result.gd_rechnung_id });
          return;
        }
        await reload();
      } catch (err: unknown) {
        onError(err instanceof Error ? err.message : "Rechnungsentwurf fehlgeschlagen.");
      }
    });
  };

  const handleLink = () => {
    void runBusy(async () => {
      try {
        if (selectedHeroInvoiceId) {
          const inv = heroInvoices.find(
            (item) =>
              String(item.customer_document_id ?? item.id ?? item.document_id ?? "") ===
              selectedHeroInvoiceId,
          );
          if (!inv) {
            onError("Bitte eine Hero-Rechnung auswählen.");
            return;
          }
          const customerId = inv.customer_document_id ?? inv.id ?? inv.document_id;
          const heroDocId = inv.hero_document_id ?? inv.document_id ?? inv.id ?? customerId;
          await linkLieferscheinHeroRechnung(dokumentId, {
            invoice_document_id: String(heroDocId),
            invoice_customer_document_id: String(customerId),
          });
        } else {
          const gdId = Number(selectedGdInvoiceId);
          if (!Number.isFinite(gdId) || gdId <= 0) {
            onError("Bitte eine Rechnung auswählen.");
            return;
          }
          const result = await linkLieferscheinRechnung(dokumentId, gdId);
          await reload();
          onRefresh();
          const docId = Number(result.gd_rechnung_id ?? gdId);
          if (Number.isFinite(docId) && docId > 0) {
            openDocumentEditor({ docId });
          }
          return;
        }
        await reload();
        onRefresh();
      } catch (err: unknown) {
        onError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Rechnung konnte nicht verknüpft werden.",
        );
      }
    });
  };

  const positions = detail?.positions || [];
  const hasInvoiceLink = Boolean(detail?.gd_rechnung_id) || Boolean(detail?.hero_invoice_number);
  const linkSelectionPending =
    (Boolean(selectedGdInvoiceId) && gdInvoicesLoading) ||
    (Boolean(selectedHeroInvoiceId) && heroInvoicesLoading);

  return (
    <div className="eh-ts-billing-layout min-h-0 w-full">
      <div className="eh-ts-billing-main space-y-2.5">
        {permissions.canBilling ? (
          <TimesheetFlowCard title="Rückfrage senden" {...cardProps}>
            {positions.length > 0 ? (
              <div className="mb-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {positions.map((position) => (
                  <TextareaInput
                    key={position.id}
                    label={positionLabel(position)}
                    rows={2}
                    value={positionQuestions[position.id] || ""}
                    onChange={(e) =>
                      setPositionQuestions((prev) => ({ ...prev, [position.id]: e.target.value }))
                    }
                    placeholder="Rückfrage…"
                  />
                ))}
              </div>
            ) : null}
            <TextareaInput
              label="Allgemeine Rückfrage"
              rows={2}
              value={generalQuestion}
              onChange={(e) => setGeneralQuestion(e.target.value)}
              placeholder="Frage zur Abrechnung…"
            />
            <Button type="button" size="sm" className="mt-1.5" disabled={busy} onClick={handleSubmitQuestions}>
              Senden
            </Button>
          </TimesheetFlowCard>
        ) : null}

        <TimesheetFlowCard
          title={`Rückfragen${questions.length ? ` (${questions.length})` : ""}`}
          {...cardProps}
        >
          {questions.length === 0 ? (
            <p className="text-sm text-label-secondary">Keine Rückfragen vorhanden.</p>
          ) : (
            <ul className="space-y-2">
              {questions.map((question) => {
                const open = questionIsOpen(question);
                return (
                  <li key={question.id} className="rounded-ios border border-separator px-2.5 py-2">
                    <p className="text-sm text-label">
                      {question.question_text || "Rückfrage zu Positionen"}
                    </p>
                    {question.response_text ? (
                      <p className="mt-1 text-xs text-label-secondary">{question.response_text}</p>
                    ) : null}
                    {(question.items || []).map((item) => (
                      <p key={item.id} className="mt-1 text-xs text-label-secondary">
                        {item.question_text}
                        {item.response_text ? ` — ${item.response_text}` : ""}
                      </p>
                    ))}
                    {permissions.canAnswerBillingQuestions && open ? (
                      <div className="mt-2">
                        <TextareaInput
                          label="Antwort"
                          rows={2}
                          value={answerText[question.id] || ""}
                          onChange={(e) =>
                            setAnswerText((prev) => ({ ...prev, [question.id]: e.target.value }))
                          }
                          placeholder="Antwort…"
                        />
                        <Button
                          type="button"
                          size="sm"
                          className="mt-1.5"
                          disabled={busy}
                          onClick={() => handleRespond(question.id)}
                        >
                          Antworten
                        </Button>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </TimesheetFlowCard>

        {permissions.canBilling ? (
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <TimesheetFlowCard title="Zuweisung" {...cardProps}>
              <NativeSelectField
                label="Bearbeiter"
                selectSize="sm"
                value={assigneeLogin}
                onChange={(e) => setAssigneeLogin(e.target.value)}
              >
                <option value="">— Auswählen —</option>
                {members.map((member) => {
                  const login = memberLogin(member);
                  if (!login) return null;
                  return (
                    <option key={login} value={login}>
                      {memberLabel(member)}
                    </option>
                  );
                })}
                {assigneeLogin && !members.some((member) => memberLogin(member) === assigneeLogin) ? (
                  <option value={assigneeLogin}>{assigneeLogin}</option>
                ) : null}
              </NativeSelectField>
              <Button
                type="button"
                size="sm"
                className="mt-1.5 w-full sm:w-auto"
                disabled={busy || !assigneeLogin}
                onClick={handleAssign}
              >
                Zuweisen
              </Button>
            </TimesheetFlowCard>

            {canCreateDraft ? (
              <TimesheetFlowCard title="Rechnungsentwurf" {...cardProps}>
                <p className="mb-2 text-xs text-label-tertiary">
                  Legt einen Rechnungsentwurf zu diesem Lieferschein an.
                </p>
                {extraCandidates.length ? (
                  <div className="mb-2 space-y-1">
                    <p className="text-xs font-semibold text-label-secondary">
                      Weitere gelieferte Lieferscheine desselben Projekts
                    </p>
                    {extraCandidates.map((candidate) => {
                      const checked = candidateIds.includes(candidate.id);
                      return (
                        <label key={candidate.id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setCandidateIds((prev) =>
                                e.target.checked
                                  ? [...prev, candidate.id]
                                  : prev.filter((id) => id !== candidate.id),
                              );
                            }}
                          />
                          {candidate.display_number}
                        </label>
                      );
                    })}
                  </div>
                ) : null}
                <Button type="button" size="sm" disabled={busy} onClick={handleInvoiceDraft}>
                  Rechnungsentwurf erstellen
                </Button>
              </TimesheetFlowCard>
            ) : null}

            {showInvoiceLink ? (
              <TimesheetFlowCard title="Rechnung verknüpfen" {...cardProps}>
                <p className="mb-2 text-xs text-label-tertiary">
                  Bestehende Rechnung aus der App oder eine Hero-Rechnung mit diesem Lieferschein
                  verknüpfen (ohne neue Positionen anzulegen).
                </p>
                {detail?.rechnung_nummer ? (
                  <p className="mb-2 text-sm text-label">
                    Verknüpft: {detail.rechnung_nummer}
                  </p>
                ) : null}
                {gdInvoicesError ? (
                  <p className="mb-2 text-xs text-status-red">{gdInvoicesError}</p>
                ) : null}
                {heroInvoicesError ? (
                  <p className="mb-2 text-xs text-status-red">{heroInvoicesError}</p>
                ) : null}
                <NativeSelectField
                  label="Rechnung (App)"
                  selectSize="sm"
                  value={selectedGdInvoiceId}
                  onChange={(e) => {
                    setSelectedGdInvoiceId(e.target.value);
                    if (e.target.value) setSelectedHeroInvoiceId("");
                  }}
                  disabled={gdInvoicesLoading || gdInvoices.length === 0}
                >
                  <option value="">
                    {gdInvoicesLoading
                      ? "Lädt…"
                      : gdInvoices.length === 0
                        ? "Keine Rechnungen im Projekt"
                        : "— Auswählen —"}
                  </option>
                  {gdInvoices.map((inv) => (
                    <option key={inv.id} value={String(inv.id)}>
                      {gdProjectInvoiceOptionLabel(inv)}
                    </option>
                  ))}
                </NativeSelectField>
                <NativeSelectField
                  label="Rechnung (Hero)"
                  selectSize="sm"
                  className="mt-2"
                  value={selectedHeroInvoiceId}
                  onChange={(e) => {
                    setSelectedHeroInvoiceId(e.target.value);
                    if (e.target.value) setSelectedGdInvoiceId("");
                  }}
                  disabled={heroInvoicesLoading || heroInvoices.length === 0}
                >
                  <option value="">
                    {heroInvoicesLoading
                      ? "Lädt…"
                      : heroInvoices.length === 0
                        ? "Keine Hero-Rechnungen im Projekt"
                        : "— Auswählen —"}
                  </option>
                  {heroInvoices.map((inv, idx) => {
                    const id = String(inv.customer_document_id ?? inv.id ?? inv.document_id ?? idx);
                    return (
                      <option key={id} value={id}>
                        {heroInvoiceOptionLabel(inv)}
                      </option>
                    );
                  })}
                </NativeSelectField>
                <Button
                  type="button"
                  size="sm"
                  className="mt-1.5 w-full sm:w-auto"
                  disabled={
                    busy ||
                    linkSelectionPending ||
                    (!selectedGdInvoiceId && !selectedHeroInvoiceId)
                  }
                  onClick={handleLink}
                >
                  {hasInvoiceLink ? "Neu verknüpfen" : "Verknüpfen"}
                </Button>
              </TimesheetFlowCard>
            ) : detail?.rechnung_nummer ? (
              <TimesheetFlowCard title="Rechnung" {...cardProps}>
                <p className="text-sm text-label">Verknüpft: {detail.rechnung_nummer}</p>
                {detail.gd_rechnung_id ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="mt-1.5"
                    onClick={() =>
                      openDocumentEditor({ docId: Number(detail.gd_rechnung_id) })
                    }
                  >
                    Rechnung öffnen
                  </Button>
                ) : null}
              </TimesheetFlowCard>
            ) : null}
          </div>
        ) : null}
      </div>
      {Number.isFinite(auftragId) && auftragId > 0 ? (
        <TimesheetBillingNotesPanel orderId={auftragId} />
      ) : null}
    </div>
  );
}
